/**
 * voice/index.ts —— 语音识别的统一出口
 *
 * 上层只认这个模块，不认具体厂商。今天用豆包，明天换别家，
 * 只要新适配器实现 SpeechProvider 接口，在这里换一行即可。
 *
 * 这层还负责一件容易漏的事：**配置来源**。设置页把 ASR 参数存在
 * localStorage（settingsRepo.getASR），上层不该关心这个细节，
 * 所以配置的读取也收在这里。
 */

import { settingsRepo } from '@/settings/settings';
import { doubaoProvider } from './doubao-stream';
import type { ASRConfig, SpeechProvider } from './provider';

export type { ASRConfig, SpeechProvider, SpeechSession } from './provider';

/** 当前选用的识别服务商。将来支持多家时，这里改成可配置 */
const provider: SpeechProvider = doubaoProvider;

/** 从设置里读一份 ASR 配置 */
export function currentConfig(): ASRConfig {
  const asr = settingsRepo.getASR();
  return {
    apiKey: asr.apiKey,
    resourceId: asr.resourceId,
    proxyUrl: asr.proxyUrl,
    language: asr.language,
  };
}

export function voiceProvider(): SpeechProvider {
  return provider;
}

/** 语音输入当前是否可用（配置齐全 + 环境支持） */
export function isVoiceAvailable(): boolean {
  return provider.isAvailable(currentConfig());
}

/** 不可用的原因（用于长按时的提示） */
export function voiceUnavailableReason(): string {
  return provider.unavailableReason(currentConfig());
}

/**
 * 为 UI 用的便捷封装：开一次识别，返回会话。
 * 抛出的错误已经是人话，可直接展示。
 */
export async function startRecognition() {
  const cfg = currentConfig();
  if (!provider.isAvailable(cfg)) {
    throw new Error(provider.unavailableReason(cfg));
  }
  return provider.start(cfg, { lang: cfg.language });
}
